/**
 * Caldwell Experiences Calendar
 * - Desktop: calendar shows a faded "peek" and expands on click
 * - Mobile/tablet: calendar is collapsed until opened
 * - Tracking: HubSpot custom behavioral events + dataLayer (GTM/GA4)
 *
 * Events fired (names are prefixed with the HubSpot prefix, e.g. pe12345678_):
 *   experiences_calendar_opened   { source }
 *   experience_viewed             { event_handle, event_title, event_date, event_type, event_status, placement }
 *   experience_clicked            { ...event props, placement, cta: reserve|waitlist }
 *
 * placement = "featured" (large Next Up card) or "calendar" (grid / list)
 */
(function () {
  'use strict';

  function track(root, name, props) {
    props = props || {};
    var prefix = (root.getAttribute('data-event-prefix') || '').trim();

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, props));
    } catch (e) {}

    if (prefix) {
      try {
        window._hsq = window._hsq || [];
        window._hsq.push(['trackCustomBehavioralEvent', {
          name: prefix + name,
          properties: props
        }]);
      } catch (e) {}
    }
  }

  function eventProps(item) {
    return {
      event_handle: item.getAttribute('data-event-handle'),
      event_title: item.getAttribute('data-event-title'),
      event_date: item.getAttribute('data-event-date'),
      event_type: item.getAttribute('data-event-type'),
      event_status: item.getAttribute('data-event-status'),
      placement: item.getAttribute('data-ec-placement') || 'calendar'
    };
  }

  function init(root) {
    if (root.__ecInit) return;
    root.__ecInit = true;

    var toggle = root.querySelector('[data-ec-toggle]');
    var panel = root.querySelector('[data-ec-panel]');
    var toggleText = root.querySelector('[data-ec-toggle-text]');
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-ec-item]'));
    var desktop = window.matchMedia('(min-width: 1025px)');
    var seen = {};
    var hasOpenedOnce = false;
    var isOpen = false;

    /* ---------- impressions ---------- */
    // Counted once per event per page view, when at least ~40% of the card is
    // actually visible (cards cut off by the peek fade count if mostly shown).
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var key = el.getAttribute('data-event-handle') + '|' + (el.getAttribute('data-ec-placement') || 'calendar');
          if (seen[key]) { observer.unobserve(el); return; }
          seen[key] = true;
          track(root, 'experience_viewed', eventProps(el));
          observer.unobserve(el);
        });
      }, { threshold: 0.4 });
      items.forEach(function (el) { observer.observe(el); });
    }

    /* ---------- open / close ---------- */
    function settleHeight(open) {
      if (!panel) return;
      if (!desktop.matches) { panel.style.maxHeight = ''; return; }

      if (open) {
        panel.style.maxHeight = panel.scrollHeight + 'px';
        var finished = false;
        var done = function (e) {
          if (e && e.propertyName && e.propertyName !== 'max-height') return;
          if (finished) return;
          finished = true;
          panel.removeEventListener('transitionend', done);
          if (isOpen) panel.style.maxHeight = 'none';
        };
        panel.addEventListener('transitionend', done);
        setTimeout(done, 800); // safety net (reduced motion / no transition)
      } else {
        // from "none" back to the peek height, animated
        panel.style.maxHeight = panel.scrollHeight + 'px';
        void panel.offsetHeight; // force reflow
        panel.style.maxHeight = '';
      }
    }

    function setOpen(open, source) {
      isOpen = open;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.classList.toggle('is-open', open);
      if (toggleText) {
        toggleText.textContent = open
          ? toggle.getAttribute('data-label-open')
          : toggle.getAttribute('data-label-closed');
      }
      settleHeight(open);

      if (open && !hasOpenedOnce) {
        // Only the first open per page view counts, so repeat toggling doesn't inflate numbers
        hasOpenedOnce = true;
        track(root, 'experiences_calendar_opened', { source: source || 'button' });
      }
    }

    if (toggle && panel) {
      toggle.addEventListener('click', function () {
        var opening = !isOpen;
        setOpen(opening, 'button');
        if (!opening && desktop.matches) {
          // keep the user oriented when the long list collapses
          var top = root.getBoundingClientRect().top;
          var panelTop = panel.getBoundingClientRect().top;
          if (panelTop < 0) panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });

      var onBreakpoint = function () {
        panel.style.maxHeight = '';
        if (isOpen && desktop.matches) panel.style.maxHeight = 'none';
      };
      if (desktop.addEventListener) desktop.addEventListener('change', onBreakpoint);
      else if (desktop.addListener) desktop.addListener(onBreakpoint);

      // Deep link: #experiences opens the full calendar
      var checkHash = function () {
        if (window.location.hash === '#experiences' || window.location.hash === '#' + root.id) {
          setOpen(true, 'deeplink');
          root.scrollIntoView({ behavior: 'smooth' });
        }
      };
      checkHash();
      window.addEventListener('hashchange', checkHash);

      if (root.getAttribute('data-start-open') === 'true') {
        hasOpenedOnce = true; // auto-open is not a visitor action
        setOpen(true, 'auto');
      }
    }

    /* ---------- CTA clicks ---------- */
    root.addEventListener('click', function (e) {
      var link = e.target.closest ? e.target.closest('[data-ec-cta]') : null;
      if (!link) return;
      var item = link.closest('[data-ec-item]');
      if (!item) return;
      var props = eventProps(item);
      props.cta = link.getAttribute('data-ec-cta');
      track(root, 'experience_clicked', props);

      // Remember the last experience clicked so the event/reservation page can report it
      try {
        sessionStorage.setItem('caldwell_last_experience', JSON.stringify({
          handle: props.event_handle,
          title: props.event_title,
          ts: Date.now()
        }));
      } catch (err) {}
    });
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-experiences-calendar]'), init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', boot);
})();